/**
 * @format
 * @module lib/audience/enterprise-access
 */
import { decrypt } from "lib/secrets";
import {
  websiteAlert,
  websiteAlertDismissListener,
  websiteAlertNodeSelector
} from "ui/templates/notifications";
import cookies from "js-cookie";
import { qs } from "utils/utils";
import logger from "utils/logger";
import differenceInMinutes from "date-fns/difference_in_minutes";
import tgamConfig from "config/tgam";
import { wasDismissed, saveDismissed, clearDismissed } from "lib/notifications/user-notifications";

const debugTag = "[ENTERPRISE-ACCESS]";

const warningAlertId = "note-access-warning"; // for warning message

const MESSAGE_WARNING =
  "Your 1 hour of access to The Globe and Mail will expire in 5 minutes. After it expires, you can start another session by clicking your access link.";

const MESSAGE_FAIL =
  "Sorry, there was an error authorizing your account. Please contact your subscription administrator.";

/**
 * Check if the current user is a partner coming from Enterprise Access page.
 * @returns {Promise<String|undefined>} of partnerId
 */
export function enterpriseAccess() {
  const searchParams = new URLSearchParams(document.location.search);

  // Required url param to continue for authentication.
  const hasPartner = searchParams.has("P");
  const partner = searchParams.get("P");
  const fusionRef = searchParams.get("ea") === "true";

  logger.log(`${debugTag} P:`, partner);

  // If user is coming directly from EA page, then clear the previously dismissed alert.
  // Since they could be using the same tab, and renewed their session.
  if (hasPartner) {
    clearDismissed(warningAlertId);
  }

  // User may be viewing page after EA page redirect or while browsing site.
  const [partnerId, encryptedId, expDate, displayName] = getAccessCookies();
  if (sessionExpiredInMinutes(expDate, 5) && !wasDismissed(warningAlertId)) {
    displayAlert("warning", MESSAGE_WARNING);
  }

  // When on homepage, assume failure when value isn't "true".
  if (hasPartner && partner !== "true") {
    displayAlert("failure", MESSAGE_FAIL);
    return Promise.resolve();
  }

  // Required all cookie values for authentication to continue.
  if (!partnerId || !encryptedId || !expDate || !displayName) {
    // Only display error message on homepage, otherwise user is on another page.
    if (hasPartner) {
      displayAlert("failure", MESSAGE_FAIL);
    }
    return Promise.resolve();
  }

  // Ensure encrypted and unencrypted partner Ids are equal.
  return decrypt(encryptedId)
    .then(decodedId => {
      const hasAccess = partnerId === decodedId;
      logger.log(`${debugTag} result:`, { partnerId, decodedId, encryptedId });
      if (!hasAccess) {
        displayAlert("failure", MESSAGE_FAIL);
        return;
      }
      // Only report this analytic once per session, so when the user first arrives on the homepage.
      if (fusionRef || hasPartner) {
        recordAnalyticsEvent(decodedId);
      }
      // always show the display name
      renderDisplayName(displayName);
      return decodedId; // success
    })
    .catch(err => {
      displayAlert("failure", MESSAGE_FAIL);
      logger.error(debugTag, err);
    });
}

/**
 * Determine if the user session will expire in X minutes
 * @param {Date} session - session expiry datetime
 * @param {Number} minutes - range when session considered expired
 * @returns {Boolean} true when expired
 */
function sessionExpiredInMinutes(session, minutes) {
  const now = new Date();
  const diff = differenceInMinutes(session, now);

  logger.log(`${debugTag} expiry:`, {
    diff: `${diff} minutes`,
    current: now.toUTCString(),
    session: session.toUTCString()
  });

  return diff >= 0 && diff <= minutes;
}

/**
 * Get the enterprise access cookie values.
 * @returns {Array<String>}
 */
function getAccessCookies() {
  const cookieOpts = {
    domain: ".theglobeandmail.com",
    path: "/"
  };
  // Match cookie names used in: setPartnerCookies()
  const partnerId = cookies.get("en_ac_auth", cookieOpts) || "";
  const encryptedId = cookies.get("en_ac_auth_enc", cookieOpts) || "";
  const expires = cookies.get("en_ac_auth_expires", cookieOpts) || "";
  const displayName = cookies.get("en_ac_auth_displayname", cookieOpts) || "";
  const expDate = new Date(expires || null); // null results in: Wed Dec 31 1969
  return [partnerId, encryptedId, expDate, displayName];
}

/**
 * Render and display a type of alert notification to the user
 * @param {String} type - of message
 * @param {String} message - for user
 */
function displayAlert(type, message) {
  insertAlert(type, renderAlert(type, message));
}

/**
 * Display an alert notification to user.
 * @param {String} type - of message
 * @param {String} alert - of HTML
 */
function insertAlert(type, alert) {
  let node = qs(websiteAlertNodeSelector);
  node = node && node.parentElement;
  if (node) {
    node.insertAdjacentHTML("beforebegin", alert);
    const container = node.closest(".pb-container");
    websiteAlertDismissListener(container, `.js-note-access-${type}__dismiss-btn`, saveDismissed);
  }
}

/**
 * Generate the warning notification message.
 * @param {String} type - of message
 * @param {String} message - for user
 * @returns {String} of HTML
 */
function renderAlert(type, message) {
  const alertId = `note-access-${type}`;
  const jsId = `js-${alertId}`;

  const dismissClasses = [
    "c-button c-button--icon-only",
    "c-button--small",
    "c-button--no-border",
    "c-button--transparent-darken",
    "c-website-alert__dismiss-btn",
    jsId + "__dismiss-btn"
  ].join(" ");

  const dismiss = /* HTML */ `<button
    class="${dismissClasses}"
    aria-label="Close alert"
    aria-controls="${jsId}"
  >
    <svg role="img" pointer-events="none" focusable="false" aria-hidden="true">
      <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
    </svg>
  </button>`;

  return websiteAlert({
    id: jsId,
    alertId,
    type: "system",
    classes: ["u-space-mts", "u-space-mbs"],
    content: `<p>${message}</p>`,
    foot: dismiss,
    wrap: "u-wrapper"
  });
}

/**
 * Push analytics event, but only once per user session.
 * @param {String} partnerId - for user
 */
function recordAnalyticsEvent(partnerId) {
  window.analyticsLayer.push({
    event: "simpleEvent",
    simpleEvent: {
      category: "simpleEvent",
      action: "enterprise access",
      label: partnerId
    }
  });
}

function renderDisplayName(name) {
  if (!window.matchMedia("(min-width: 768px)").matches || !name.length) {
    return;
  }
  const display = name.length > 25 ? `${name.slice(0, 24)}…` : name;
  const el = document.querySelector(".js-ea-displayname");
  el.innerHTML = `<p id="ea-displayname">Access compliments of<br>${display}</p>`;
  el.classList.remove("u-hidden");
}
